import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "report-page" }
const _hoisted_2 = {
  key: 0,
  class: "error-message"
}
const _hoisted_3 = { class: "report-inputs" }
const _hoisted_4 = { class: "form-group" }
const _hoisted_5 = { class: "date-input-wrapper" }
const _hoisted_6 = { class: "calendar-icon" }
const _hoisted_7 = { class: "form-group" }
const _hoisted_8 = { class: "date-input-wrapper" }
const _hoisted_9 = { class: "calendar-icon" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = {
  id: "summary_document_container",
  style: {"display":"none"}
}
const _hoisted_13 = { id: "summary_document" }
const _hoisted_14 = { class: "document_header" }
const _hoisted_15 = ["src"]
const _hoisted_16 = { class: "document_body" }
const _hoisted_17 = { class: "report_stats_info" }
const _hoisted_18 = { class: "food_entry" }
const _hoisted_19 = { class: "table-wrapper" }
const _hoisted_20 = { class: "summary_table" }
const _hoisted_21 = { key: 0 }

import { ref } from 'vue';
import { useStore } from 'vuex';
import { fetchWithAuth } from '@/utils/api';
import html2pdf from 'html2pdf.js';
import logoImage from '@/assets/images/brave_bites_new_logo.png';


export default /*@__PURE__*/_defineComponent({
  __name: 'Report',
  setup(__props) {

const store = useStore();
const apiUrl = store.getters.getApiUrl;

const loading = ref(false);
const from_date = ref("");
const to_date = ref("");
const validationError = ref("");


const reportData = ref([]);

const generateSummary = async () => {
  if (!from_date.value || !to_date.value) {
    validationError.value = "Both start and end dates must be provided.";
    return;
  }
  validationError.value = "";

  try {
    await fetchReportData();
    // Automatically generate and download the PDF after data is ready
    setTimeout(() => {
      generatePdf();
    }, 500); // slight delay ensures DOM has rendered
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};


function clear() {
  reportData.value = [];
}

const generatePdf = () => {
  const container = document.getElementById('summary_document_container');
  const element = document.getElementById('summary_document');
  if (!container || !element) return;

  // Add pdf-exporting class to hide the container but keep it in the DOM
  container.classList.add('pdf-exporting');

  const opt = {
    margin: 0.5,
    filename: `exposure-summary-${from_date.value}_to_${to_date.value}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
    pagebreak: {
      mode: ['css', 'legacy']
    }
  };

  html2pdf()
    .set(opt)
    .from(element)
    .outputPdf('blob') // Generate PDF as a Blob instead of auto-saving
    .then((pdfBlob) => {
      // Create a blob URL for download
      const blobUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = opt.filename;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      container.classList.remove('pdf-exporting');
    });
};


const getAverageAnxiety = () => {
  const exposuresAmount = getAmountOfExposures();

  let anxietyTotal = 0;

  reportData.value.forEach(food => {
    food.logs.forEach(log => {
      let temp = 0;
      temp += Number(log.anxiety_level);
      anxietyTotal += temp;
    });
  });

  return (anxietyTotal / exposuresAmount).toFixed(1);
}

const getAmountOfExposures = () => {
  let totalExposures = 0;

  reportData.value.forEach(food => {
    totalExposures += food.logs.length;
  });

  return totalExposures;
};

const getAmountOfDifferentFoods = () => {
  return reportData.value.length;
}

function formatDateRange(fromDateStr: string | null, toDateStr: string | null) {
  if (!fromDateStr || !toDateStr) {
    return '';
  }

  const fromDate = new Date(fromDateStr);
  const toDate = new Date(toDateStr);

  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const yearOptions: Intl.DateTimeFormatOptions = { year: 'numeric' };

  const sameMonth = fromDate.getMonth() === toDate.getMonth();
  const sameYear = fromDate.getFullYear() === toDate.getFullYear();

  const fromFormatted = fromDate.toLocaleDateString('en-US', options);
  const toFormatted = toDate.toLocaleDateString('en-US', options);
  const year = toDate.toLocaleDateString('en-US', yearOptions);

  if (sameMonth && sameYear) {
    return `${fromFormatted} – ${toFormatted}, ${year}`;
  } else if (sameYear) {
    const fromWithMonth = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const toWithMonth = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fromWithMonth} – ${toWithMonth}, ${year}`;
  } else {
    const fromFull = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const toFull = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fromFull} – ${toFull}`;
  }
}


const fetchReportData = async () => {
  if (!from_date.value || !to_date.value) {
    alert('Start date and end date must be provided');
    return;
  }

  loading.value = true;

  try {
    const response = await fetchWithAuth(apiUrl + `/api/dishes/report?start_date=${from_date.value}&end_date=${to_date.value}`, {
      method: 'GET'
    }, store);
    if (response.ok) {
      const data = await response.json();
      reportData.value = data;
    } else {
      alert("Error while fetching report data");
      console.error('Failed to fetch report data:', await response.text());
    }

    loading.value = false;

  } catch (error) {
    alert("Error while fetching report data");
    loading.value = false;
  }
};


return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[12] || (_cache[12] = _createElementVNode("div", { class: "info-card" }, [
      _createElementVNode("h2", null, "📝 Your Exposure Journey"),
      _createElementVNode("p", null, " Select a date range to generate a personal progress report of all your exposures. This can help you and your therapist understand your progress and challenges. ")
    ], -1)),
    (validationError.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(validationError.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _cache[2] || (_cache[2] = _createElementVNode("label", { for: "from-date-input" }, "From", -1)),
        _createElementVNode("div", _hoisted_5, [
          _withDirectives(_createElementVNode("input", {
            id: "from-date-input",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((from_date).value = $event)),
            type: "date"
          }, null, 512), [
            [_vModelText, from_date.value]
          ]),
          _createElementVNode("span", _hoisted_6, [
            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'calendar-alt'] })
          ])
        ]),
        _cache[3] || (_cache[3] = _createElementVNode("small", { class: "input-hint" }, "Tap to select a date", -1))
      ]),
      _createElementVNode("div", _hoisted_7, [
        _cache[4] || (_cache[4] = _createElementVNode("label", { for: "to-date-input" }, "To", -1)),
        _createElementVNode("div", _hoisted_8, [
          _withDirectives(_createElementVNode("input", {
            id: "to-date-input",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((to_date).value = $event)),
            type: "date"
          }, null, 512), [
            [_vModelText, to_date.value]
          ]),
          _createElementVNode("span", _hoisted_9, [
            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'calendar-alt'] })
          ])
        ]),
        _cache[5] || (_cache[5] = _createElementVNode("small", { class: "input-hint" }, "Tap to select a date", -1))
      ]),
      _createElementVNode("button", {
        onClick: generateSummary,
        class: "button primary"
      }, [
        (!loading.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_10, "Create Report"))
          : _createCommentVNode("", true),
        (loading.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_11, "Generating your report..."))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_12, [
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("div", _hoisted_14, [
          _createElementVNode("div", null, [
            _createElementVNode("img", { src: _unref(logoImage) }, null, 8, _hoisted_15)
          ]),
          _createElementVNode("div", null, [
            _cache[6] || (_cache[6] = _createElementVNode("h1", null, "Exposure Summary Report", -1)),
            _createElementVNode("h2", null, _toDisplayString(formatDateRange(from_date.value, to_date.value)), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("div", null, [
              _createElementVNode("p", null, _toDisplayString(getAmountOfExposures()), 1),
              _cache[7] || (_cache[7] = _createElementVNode("small", null, "Exposures Completed", -1))
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("p", null, _toDisplayString(getAmountOfDifferentFoods()), 1),
              _cache[8] || (_cache[8] = _createElementVNode("small", null, "Dishes Tackled", -1))
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("p", null, _toDisplayString(getAverageAnxiety()), 1),
              _cache[9] || (_cache[9] = _createElementVNode("small", null, "Avg. Anxiety (1–10)", -1))
            ])
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reportData.value, (food, index) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_18, [
              _createElementVNode("p", null, _toDisplayString(food.title), 1),
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("table", _hoisted_20, [
                  _cache[10] || (_cache[10] = _createElementVNode("thead", null, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", null, "Date"),
                      _createElementVNode("th", null, "Anxiety Level"),
                      _createElementVNode("th", null, "Taste"),
                      _createElementVNode("th", { class: "big-col" }, "Bodys reaction"),
                      _createElementVNode("th", { class: "big-col" }, "Additional notes")
                    ])
                  ], -1)),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(food.logs, (log) => {
                      return (_openBlock(), _createElementBlock("tr", null, [
                        _createElementVNode("td", null, _toDisplayString(log.date), 1),
                        _createElementVNode("td", null, _toDisplayString(log.anxiety_level), 1),
                        _createElementVNode("td", null, _toDisplayString(log.taste), 1),
                        _createElementVNode("td", null, _toDisplayString(log.bodys_reaction), 1),
                        _createElementVNode("td", null, _toDisplayString(log.additional_notes), 1)
                      ]))
                    }), 256))
                  ])
                ])
              ]),
              (index !== reportData.value.length -1)
                ? (_openBlock(), _createElementBlock("hr", _hoisted_21))
                : _createCommentVNode("", true)
            ]))
          }), 256))
        ]),
        _cache[11] || (_cache[11] = _createElementVNode("small", null, "Created with Brave Bites", -1))
      ])
    ])
  ]))
}
}

})